/**
 * Escolha da incorporadora. É a raiz da navegação depois de entrar.
 *
 * Quem enxerga o quê é decidido pela RLS: `listarEmpresas()` já devolve só o permitido.
 * A tela não recalcula regra de acesso — só mostra, e deixa explícito quando o usuário
 * está ali como plataforma: o Admin ("Admin Fincast" na tela) entra sem ser da equipe e,
 * desde a 0031 (12/10/2026), com os poderes de gestor em toda incorporadora ativa.
 */
import { h } from '../dom';
import { icone } from '../icones';
import { cabecalhoDeTela, tentar, vazio } from '../casca';
import type { Vista } from '../casca';
import { avisar, confirmar, formulario, perguntar } from '../ui/aviso';
import { botaoMenu } from '../ui/menu';
import { ir, recarregar } from '../rotas';
import {
  atualizarEmpresa, criarEmpresa, listarEmpresas, pedirExclusao,
} from '../../../../packages/dados/src/repositorios';
import type { Empresa } from '../../../../packages/dados/src/repositorios';
import { podeAdministrar } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';
import { seloDoMembro, tituloDoAcessoDePlataforma } from '../acesso-plataforma';

const NOME_PAPEL: Record<string, string> = {
  gestor: 'Gestor',
  analista: 'Analista',
  consulta: 'Consulta',
};

/** "3 empreendimentos" — e "nenhum empreendimento ainda" em vez de "0". */
function contagem(n: number): string {
  if (n === 0) return 'nenhum empreendimento ainda';
  return n === 1 ? '1 empreendimento' : `${n} empreendimentos`;
}

function editarCadastro(e: Empresa): void {
  void (async () => {
    const dados = await formulario({
      titulo: 'Editar cadastro',
      descricao: 'Estes dados aparecem na lista e identificam a incorporadora.',
      campos: [
        { chave: 'nome', rotulo: 'Nome', valor: e.nome, obrigatorio: true },
        { chave: 'cnpj', rotulo: 'CNPJ', valor: e.cnpj ?? '', dica: '00000000000000', ajuda: 'Só os 14 números. Deixe em branco para não informar.' },
      ],
      confirmar: 'Salvar',
    });
    if (dados === null) return;
    const nome = dados['nome'] ?? '';
    const cnpj = dados['cnpj'] ?? '';
    if (nome === e.nome && cnpj === (e.cnpj ?? '')) return;
    await tentar(
      () => atualizarEmpresa(e.id, { nome, cnpj }),
      () => {
        avisar('Cadastro atualizado.');
        recarregar();
      },
    );
  })();
}

function solicitarExclusaoEmpresa(e: Empresa): void {
  void (async () => {
    const ok = await confirmar({
      titulo: 'Pedir a exclusão desta incorporadora?',
      corpo: [
        'Você vai enviar um pedido para excluir ',
        h('b', {}, e.nome),
        ', com tudo o que existe dentro dela. Quem decide é o administrador da plataforma — nada some agora.',
      ],
      confirmar: 'Enviar pedido',
      perigo: true,
    });
    if (!ok) return;
    await tentar(
      () => pedirExclusao({ alvo: 'empresa', empresaId: e.id }),
      () => avisar('Pedido enviado. O administrador da plataforma vai avaliar.'),
    );
  })();
}

function cartaoEmpresa(e: Empresa, perfil: Perfil): HTMLElement {
  /* ADM-4: o Admin membro (de qualquer papel) aparece como "Admin Fincast", com o papel no title */
  const doMembro = seloDoMembro(NOME_PAPEL[e.meuPapel ?? ''] ?? 'Membro', perfil.isAdminHype);
  const selo = e.souMembro
    ? h('span', { class: 'badge sec', title: doMembro.titulo }, doMembro.texto)
    : h('span', {
        class: 'badge out',
        /* ADM-3: "com os poderes de gestor" só na ativa — ver `acesso-plataforma.ts` */
        title: tituloDoAcessoDePlataforma(e.ativa),
      }, 'Acesso de plataforma');

  /* Editar o cadastro e pedir a exclusão: gestor da incorporadora OU o Admin (0031) — o
     mesmo `podeAdministrar` do resto do app, com a situação lida agora (`e.ativa`, desta
     lista) valendo sobre o perfil em memória. Em incorporadora inativa, ninguém: o card já
     diz "nada pode ser feito nela". Para o Admin, pedir a exclusão segue o caminho de
     todo mundo (D22 = A): o pedido cai na fila, e ele aprova lá. */
  const menu = podeAdministrar(perfil, e.id, e.ativa)
    ? botaoMenu(() => [
        { rotulo: 'Editar cadastro', aoClicar: () => editarCadastro(e) },
        { rotulo: 'Solicitar exclusão', perigo: true, aoClicar: () => solicitarExclusaoEmpresa(e) },
      ])
    : null;

  /* Era um <button>, e tinha um botão dentro assim que o ⋮ entrou: HTML inválido, e
     clique e teclado ficam ambíguos. As outras duas listas já usavam <div>. */
  return h(
    'div',
    {
      class: 'card acard',
      style: 'padding:18px;display:flex;align-items:center;gap:14px;text-align:left;cursor:pointer;width:100%',
      onclick: () => ir({ nome: 'empreendimentos', empresaId: e.id }),
    },
    h('span', { style: 'color:hsl(var(--primary-text));flex:none', html: icone.empresa(22) }),
    h(
      'span',
      { style: 'display:grid;gap:3px;min-width:0;flex:1' },
      h('span', { style: 'font-weight:640' }, e.nome),
      h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' },
        `${e.ativa ? 'Ativa' : 'Inativa — nada pode ser feito nela'} · ${contagem(e.empreendimentos)}`),
    ),
    selo,
    menu,
    h('span', { class: 'muted', style: 'flex:none', html: icone.seta(16) }),
  );
}

export async function telaIncorporadoras(perfil: Perfil): Promise<Vista> {
  const empresas = await listarEmpresas();

  const botaoCriar = perfil.isAdminHype
    ? h(
        'button',
        {
          class: 'btn pri',
          'data-el': 'EL-173',
          onclick: () => {
            void (async () => {
              const nome = await perguntar({
                titulo: 'Nova incorporadora',
                rotulo: 'Nome da incorporadora',
                dica: 'Ex.: Construtora Tramontto',
              });
              if (nome === null) return;
              await tentar(
                () => criarEmpresa({ nome }),
                (nova) => {
                  /* Direto para a equipe: uma incorporadora sem ninguém dentro não serve
                     para nada, e o convite ao primeiro gestor é o passo seguinte
                     obrigatório. Deixar o usuário de volta na lista faria esse passo
                     parecer opcional. */
                  avisar(`Incorporadora "${nova.nome}" criada. Agora convide o primeiro gestor.`);
                  ir({ nome: 'equipe', empresaId: nova.id });
                },
              );
            })();
          },
        },
        h('span', { html: icone.mais(15) }),
        'Nova incorporadora',
      )
    : null;

  const corpo = h('div', { 'data-el': 'EL-172' });
  corpo.appendChild(
    cabecalhoDeTela(
      'Incorporadoras',
      perfil.isAdminHype
        ? 'Você enxerga todas as incorporadoras da plataforma. Entrar em uma delas fica registrado.'
        : 'Escolha em qual incorporadora você quer trabalhar.',
      botaoCriar,
    ),
  );

  if (empresas.length === 0) {
    corpo.appendChild(
      vazio(
        perfil.isAdminHype
          ? 'Nenhuma incorporadora cadastrada ainda.'
          : 'Você ainda não faz parte de nenhuma incorporadora. Peça acesso ao gestor.',
      ),
    );
  } else {
    corpo.appendChild(
      h('div', { style: 'display:grid;gap:10px;max-width:760px' }, ...empresas.map((e) => cartaoEmpresa(e, perfil))),
    );
  }

  return { trilha: [{ texto: 'Incorporadoras' }], corpo, ativo: 'incorporadoras' };
}
